import {
  AutoStack,
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { FormikBooleanChoiceGroup } from 'components/BooleanChoiceGroup/BooleanChoiceGroup'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { FieldProps } from 'formik'
import {
  FormikChoiceGroup,
  FormikDropdown,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Box } from 'grid'
import { canUpdateProving, formatEqNr, isInProvingRole } from 'helpers'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useEqVehiclesByLabelQuery } from 'hooks/useEqVehicleModels.generated'
import { EqVehicle, InternalVehicle, ProvingUserRole } from 'model'
import {
  DayOfWeek,
  IChoiceGroupOption,
  IDropdownOption,
  ISuggestionItemProps,
  ITag,
  Label,
  Stack,
  TagItem,
  Text,
  TextField,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDoubleBookingsVehiclesQuery } from 'views/ProvingDetailsView/hooks'
import * as Yup from 'yup'

export class ProvingDetailsInternalVehicleFormSchema {
  constructor() {
    return Yup.object().shape({
      unknown: Yup.boolean(),
      licensePlateType: Yup.string(),
      model: Yup.string().required('Modell ist ein Pflichtfeld'),
      eqVehicle: Yup.string().when('unknown', {
        is: false,
        then: Yup.string().required(),
        otherwise: Yup.string(),
      }),
      dateFrom: Yup.date().required(),
      dateTo: Yup.date()
        .required()
        .when(
          'dateFrom',
          (dateFrom: any, schema: any) => dateFrom && schema.min(dateFrom)
        ),
      orderId: Yup.string().required(),
    })
  }
}

export const ProvingDetailsInternalVehicleForm: React.FC<{
  editVehicle?: InternalVehicle
  invoked?: boolean
}> = ({ editVehicle, invoked }) => {
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const [licensePlateKnown, setLicensePlateKnown] = React.useState()
  const [isUnknownVehicle, setIsUnknownVehicle] = React.useState(false)
  const { data: allDoublebookings } = useDoubleBookingsVehiclesQuery()

  const orderIds = proving.orderIdDistribution
    ? proving.orderIdDistribution.map((oId) => oId)
    : []

  const { data, loading } = useEqVehiclesByLabelQuery()
  const eqVehiclesData = data && data.eqVehiclesByLabel

  const eqVehicleOptions = React.useMemo(
    () =>
      eqVehiclesData &&
      eqVehiclesData.map((v: any) => ({
        name: `${v.id || ''}||${v.label || ''}||${v.vin || ''}||${
          v.licensePlate || ''
        }||${v.derivative || ''}`,
        key: v._id,
        model: v.type,
      })),
    [eqVehiclesData]
  )

  const licensePlateTypeOptions: IChoiceGroupOption[] = [
    {
      key: 'known',
      text: t('field.vehicle.known'),
      styles: { choiceFieldWrapper: { paddingRight: '15px' } },
    },
    {
      key: 'unknown',
      text: t('field.vehicle.unknown'),
      styles: { choiceFieldWrapper: { paddingRight: '15px' } },
    },
    { key: 'noRegistration', text: t('field.vehicle.noRegistration') },
  ]

  const [vehicleKnownOption, vehicleUnknownOption]: IChoiceGroupOption[] = [
    {
      key: 'known',
      text: t('field.vehicle.vehicleKnown'),
    },
    {
      key: 'unknown',
      text: t('field.vehicle.vehicleUnknown'),
    },
  ]

  const setEqVehicleValues = (
    fieldProps: FieldProps<any>,
    eqVehicle: EqVehicle | undefined
  ) => {
    if (eqVehicle) {
      fieldProps.form.setFieldValue('label', eqVehicle.label || '')
      fieldProps.form.setFieldValue(
        'licensePlate',
        eqVehicle.licensePlate || ''
      )
      fieldProps.form.setFieldValue('vin', eqVehicle.vin || '')
      fieldProps.form.setFieldValue('model', eqVehicle.type || '')
    }
  }

  const renderSuggestionsItem = (
    props: ITag,
    _: ISuggestionItemProps<ITag>
  ) => {
    const [id, label, vin, licensePlate, derivative] = props.name.split('||')
    // Columns have a fixed width, so anything too long has to be cut off
    // instead of running into the next column.
    const truncate = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    }
    return (
      <AutoStack horizontal={true}>
        <Box width={90} marginBottom={1} style={truncate} title={id}>
          {formatEqNr(id)}
        </Box>
        <Box
          width={180}
          marginBottom={1}
          style={{ ...truncate, fontWeight: 'bold' }}
          title={label}
        >
          {label}
        </Box>
        <Box width={160} marginBottom={1} style={truncate} title={vin}>
          {vin}
        </Box>
        <Box width={120} marginBottom={1} style={truncate} title={licensePlate}>
          {licensePlate}
        </Box>
        <Box width={120} marginBottom={1} style={truncate} title={derivative}>
          {derivative}
        </Box>
      </AutoStack>
    )
  }

  const canUpdateCurrentProving = canUpdateProving(proving)
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const userCanUpdate = () => {
    if (proving.status === 8 || proving.status === 9) {
      return false
    } else if (isMaintenanceHead) {
      return false
    } else if (canUpdateCurrentProving) {
      return true
    } else if (
      isProvingManager &&
      (proving.status === 1 ||
        proving.status === 4 ||
        proving.status === 5 ||
        proving.status === 10)
    ) {
      return true
    } else {
      return false
    }
  }
  const result =
    allDoublebookings && allDoublebookings.cachedProvings
      ? allDoublebookings.cachedProvings
      : []
  const [resultArray, setResultArray] = useState<any[]>([])

  const checkForDoublebooking = (
    vehicleId: string,
    dateFrom: string,
    dateTo: string
  ) => {
    const doublebookings = [] as any[]
    result.forEach((prov: any) =>
      ((prov.vehicles as InternalVehicle[]) || []).forEach((vhc) => {
        if (
          (vhc && vhc.eqVehicle && vhc.eqVehicle ? vhc.eqVehicle._id : '') ===
          vehicleId
        ) {
          if (vhc.dateFrom <= dateTo && vhc.dateTo >= dateFrom) {
            doublebookings.push({
              provingGroundTitle: prov.provingGround!.title,
              provingDescription: prov.title,
              provingNr: prov.id,
              vehicle: vhc,
            })
          }
        }
      })
    )
    setResultArray(doublebookings)
  }

  return (
    <FlexSpinner loading={loading}>
      <FormStack>
        {invoked && <Label>{t('field.vehicle.addPAGVehicle')}</Label>}
        <FieldStack>
          <FieldStackItem
            name="unknown"
            render={(fieldProps) => {
              setIsUnknownVehicle(fieldProps.field.value)
              return (
                <FormikBooleanChoiceGroup
                  trueOption={vehicleUnknownOption}
                  falseOption={vehicleKnownOption}
                  falseOptionFirst={true}
                  horizontal={true}
                  fieldProps={fieldProps}
                  disabled={!userCanUpdate()}
                />
              )
            }}
          />
        </FieldStack>
        <FieldStack>
          {/* <FieldStackItem
            name="model"
            required={true}
            render={fieldProps => {
              return (
                <FormikComboBox
                  label={t('field.vehicle.vehicleModel')}
                  options={modelOptions}
                  onChange={(_, option) => {
                    if (option) {
                      setSelectedModel(option.key)
                    }
                  }}
                  {...fieldProps}
                  disabled={!userCanUpdate()}
                  required={true}
                  placeholder="Bitte wählen Sie zuerst ein Modell aus der Liste aus."
                />
              )
            }}
          /> */}

          {!isUnknownVehicle && (
            <FieldStackItem
              name="eqVehicle"
              render={(fieldProps) => {
                return (
                  <>
                    <FormikTagPicker
                      label={t('field.vehicle.labelPAGVehicle')}
                      tags={eqVehicleOptions}
                      suggestionLimit={50}
                      resolveDelay={250}
                      suggestionsHeaderText={t(
                        'Die ersten 50 Ergebnisse werden angezeigt'
                      )}
                      // onResolveSuggestions={resolveEqVehicles as any}
                      onChange={(item) => {
                        setEqVehicleValues(
                          fieldProps,
                          eqVehiclesData.find((v: any) => v._id === item.key)
                        )
                        const dateFrom = fieldProps.form.values.dateFrom
                        const dateTo = fieldProps.form.values.dateTo
                        checkForDoublebooking(item.key, dateFrom, dateTo)
                      }}
                      onRenderSuggestionsItem={renderSuggestionsItem}
                      onRenderItem={(props) => {
                        const [id, label] = props.item.name.split('||')
                        return <TagItem {...props}>{label || id}</TagItem>
                      }}
                      {...fieldProps}
                      disabled={!userCanUpdate()}
                      required={true}
                    />
                  </>
                )
              }}
            />
          )}
          <FieldStackItem
            name="model"
            label={t('field.vehicle.vehicleModel')}
            component={FormikTextField}
            disabled={!userCanUpdate()}
            placeholder={
              !isUnknownVehicle ? `Feld wird automatisch befüllt.` : ``
            }
          />
        </FieldStack>
        {resultArray && resultArray.length > 0 && (
          <FieldStack>
            <Text variant="xLarge" as="h2">
              ACHTUNG!
            </Text>
            <Stack.Item>
              <Label>
                {t(
                  `Folgendes Fahrzeug ist zum Zeitraum ${onFormatDate(
                    new Date(proving.dateFrom)
                  )} bis ${onFormatDate(
                    new Date(proving.dateTo)
                  )} bereits gebucht`
                )}
              </Label>
              {resultArray.map((x: any, i: number) => {
                return (
                  <FieldStack key={i}>
                    <Label>{x.vehicle.label}</Label>
                    <table>
                      <tbody>
                        <tr>
                          <td>Standort: {x.provingGroundTitle}</td>
                        </tr>
                        <tr>
                          <td>Erprobungsnr.: {x.provingNr}</td>
                        </tr>
                        <tr>
                          <td>Erprobung: {x.provingDescription}</td>
                        </tr>
                        <tr>
                          <td>
                            Datum: {onFormatDate(new Date(x.vehicle.dateFrom))}{' '}
                            bis {onFormatDate(new Date(x.vehicle.dateTo))}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <hr />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </FieldStack>
                )
              })}
              <Label>{t('Möchten Sie das Fahrzeug trotzdem buchen?')}</Label>
            </Stack.Item>
          </FieldStack>
        )}
        {!isUnknownVehicle && (
          <>
            <FieldStack>
              <FieldStack>
                <FieldStackItem
                  name="licensePlateType"
                  render={(fieldProps) => {
                    setLicensePlateKnown(fieldProps.field.value === 'known')
                    return (
                      <FormikChoiceGroup
                        {...fieldProps}
                        label={t('field.vehicle.licensePlate.label')}
                        onChanged={(option) => {
                          if (option.key !== 'known') {
                            fieldProps.form.setFieldValue(
                              'licensePlate',
                              undefined
                            )
                          }
                        }}
                        styles={{ flexContainer: { display: 'flex' } }}
                        options={licensePlateTypeOptions}
                        defaultSelectedKey={fieldProps.field.value}
                        disabled={!userCanUpdate()}
                      />
                    )
                  }}
                />
                {licensePlateKnown && (
                  <FieldStackItem
                    name="licensePlate"
                    component={FormikTextField}
                    disabled={!userCanUpdate()}
                    placeholder="Feld wird automatisch befüllt."
                  />
                )}
              </FieldStack>
            </FieldStack>
            <FieldStack>
              <FieldStack>
                <FieldStackItem
                  name="vin"
                  label={t('field.vehicle.vin')}
                  component={FormikTextField}
                  disabled={!userCanUpdate()}
                  placeholder="Feld wird automatisch befüllt."
                />
              </FieldStack>
            </FieldStack>
          </>
        )}
        {isUnknownVehicle && (
          <>
            <TextField
              readOnly={true}
              label={t('field.vehicle.internalNumber.label')}
              value={t('field.vehicle.internalNumber.value')}
              disabled={!userCanUpdate()}
            />
            <TextField
              readOnly={true}
              label={t('field.vehicle.licensePlate.label')}
              value={t('field.vehicle.licensePlate.value')}
              disabled={!userCanUpdate()}
            />
          </>
        )}

        <FieldStack horizontal={true} wrap={true}>
          <FieldStackItem
            name="dateFrom"
            label={t('field.dateFrom.label')}
            isRequired={true}
            value={proving.dateFrom}
            component={FormikGraphQLDatePicker}
            // minDate={new Date(proving.dateFrom)}
            // maxDate={new Date(proving.dateTo)}
            firstDayOfWeek={DayOfWeek.Monday}
            strings={DayPickerStrings}
            showWeekNumbers={true}
            formatDate={onFormatDate}
            disabled={!userCanUpdate()}
            initialPickerDate={new Date(proving.dateFrom)}
          />
          <FieldStackItem
            name="dateTo"
            label={t('field.dateTo.label')}
            isRequired={true}
            component={FormikGraphQLDatePicker}
            // minDate={new Date(proving.dateFrom)}
            // maxDate={new Date(proving.dateTo)}
            firstDayOfWeek={DayOfWeek.Monday}
            strings={DayPickerStrings}
            showWeekNumbers={true}
            formatDate={onFormatDate}
            disabled={!userCanUpdate()}
            initialPickerDate={new Date(proving.dateFrom)}
          />
        </FieldStack>
        <FieldStack>
          <Label required={true}>{t('field.orderId.label')}</Label>
          <FieldStackItem
            name="orderId"
            label={t('field.orderId.label')}
            isRequired={true}
            render={(fieldProps) => (
              <FormikDropdown
                {...fieldProps}
                disabled={!userCanUpdate()}
                options={[
                  { key: '', text: t('field.vehicle.noOrderId') },
                  ...orderIds.map(
                    (orderId) =>
                      ({
                        key: orderId.orderId,
                        text: `${orderId.orderId} - ${
                          orderId.annotation ? `${orderId.annotation} ` : ''
                        }(${orderId.percentage} %)`,
                      } as IDropdownOption)
                  ),
                ]}
              />
            )}
          />
        </FieldStack>
      </FormStack>
    </FlexSpinner>
  )
}
